import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext.tsx';
import { useCart } from '../../context/CartContext.tsx';

export default function Navbar() {
  const { user, hasAdminAccess, logout } = useAuth();
  const { itemCount, fetchCart, cartVersion } = useCart();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [badgePop, setBadgePop] = useState(false);
  const prevVersion = useRef(cartVersion);

  // Fetch cart on mount and when location changes
  useEffect(() => {
    if (user) {
      fetchCart();
    }
  }, [user, location.pathname]);

  // Trigger pop animation whenever cartVersion bumps
  useEffect(() => {
    if (cartVersion > prevVersion.current) {
      setBadgePop(true);
      const t = setTimeout(() => setBadgePop(false), 500);
      return () => clearTimeout(t);
    }
    prevVersion.current = cartVersion;
  }, [cartVersion]);

  const isActive = (path: string) => location.pathname.startsWith(path);

  return (
    <nav className="sticky top-0 z-50 glass shadow-md">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 shrink-0 group">
            <img
              src="/FormalLogoFinal-Square.png"
              alt="Runninglime"
              className="h-9 w-9 rounded-lg transition-transform duration-300 group-hover:scale-110"
            />
            <span className="text-lg font-bold text-dark hidden sm:block">
              Beverages<span className="text-green"> by Runninglime</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            <NavLink to="/" active={location.pathname === '/'}>Home</NavLink>
            {user && (
              <>
                <NavLink to="/store" active={isActive('/store')}>Store</NavLink>
                <NavLink to="/orders" active={isActive('/orders')}>Orders</NavLink>
                <NavLink to="/support" active={isActive('/support')}>Support</NavLink>
                {hasAdminAccess && (
                  <NavLink to="/admin" active={isActive('/admin')}>Admin</NavLink>
                )}
              </>
            )}
          </div>

          {/* Right Side */}
          <div className="flex items-center gap-3">
            {user ? (
              <>
                {/* Cart */}
                <Link
                  to="/cart"
                  className="relative p-2 rounded-xl text-dark hover:bg-lime/20 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 00-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 00-16.536-1.84M7.5 14.25L5.106 5.272M6 20.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zm12.75 0a.75.75 0 11-1.5 0 .75.75 0 011.5 0z" />
                  </svg>
                  {itemCount > 0 && (
                    <span
                      className={`absolute -top-1 -right-1 bg-accent text-dark text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center transition-transform ${
                        badgePop ? 'animate-cart-pop' : ''
                      }`}
                    >
                      {itemCount > 99 ? '99+' : itemCount}
                    </span>
                  )}
                </Link>

                {/* User Menu */}
                <div className="relative">
                  <button
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className="flex items-center gap-2 p-2 rounded-xl hover:bg-lime/20 transition-all duration-200 group"
                  >
                    <div className="h-8 w-8 rounded-full bg-gradient-to-br from-lime to-green flex items-center justify-center text-dark font-bold text-sm transition-transform duration-200 group-hover:scale-105">
                      {user.contactName?.charAt(0) || user.email.charAt(0).toUpperCase()}
                    </div>
                    <span className="hidden lg:block text-sm font-medium text-dark">
                      {user.contactName || user.email}
                    </span>
                    <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {userMenuOpen && (
                    <div className="absolute right-0 mt-2 w-48 glass rounded-xl shadow-xl py-1 z-50 animate-scale-in origin-top-right">
                      <div className="px-4 py-2 border-b border-gray-200">
                        <p className="text-sm font-medium text-dark">{user.contactName}</p>
                        <p className="text-xs text-gray-500">{user.companyName}</p>
                      </div>
                      <Link
                        to="/orders"
                        onClick={() => setUserMenuOpen(false)}
                        className="block px-4 py-2 text-sm text-dark hover:bg-lime/20 transition-colors"
                      >
                        My Orders
                      </Link>
                      <Link
                        to="/support"
                        onClick={() => setUserMenuOpen(false)}
                        className="block px-4 py-2 text-sm text-dark hover:bg-lime/20 transition-colors"
                      >
                        Support
                      </Link>
                      <Link
                        to="/billing"
                        onClick={() => setUserMenuOpen(false)}
                        className="block px-4 py-2 text-sm text-dark hover:bg-lime/20 transition-colors"
                      >
                        Billing & Shipping
                      </Link>
                      <Link
                        to="/change-password"
                        onClick={() => setUserMenuOpen(false)}
                        className="block px-4 py-2 text-sm text-dark hover:bg-lime/20 transition-colors"
                      >
                        Change Password
                      </Link>
                      <button
                        onClick={() => { logout(); setUserMenuOpen(false); }}
                        className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                      >
                        Sign Out
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="flex items-center gap-2">
                <Link
                  to="/login"
                  className="px-4 py-2 text-sm font-medium text-dark hover:text-green transition-colors"
                >
                  Sign In
                </Link>
                <Link
                  to="/register"
                  className="btn-gradient px-4 py-2 text-sm rounded-xl"
                >
                  Get Started
                </Link>
              </div>
            )}

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-lime/20 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {mobileOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="md:hidden pb-4 border-t border-gray-200 mt-2 pt-3 space-y-1 animate-slide-down">
            <MobileLink to="/" onClick={() => setMobileOpen(false)}>Home</MobileLink>
            {user && (
              <>
                <MobileLink to="/store" onClick={() => setMobileOpen(false)}>Store</MobileLink>
                <MobileLink to="/orders" onClick={() => setMobileOpen(false)}>Orders</MobileLink>
                <MobileLink to="/support" onClick={() => setMobileOpen(false)}>Support</MobileLink>
                {hasAdminAccess && (
                  <MobileLink to="/admin" onClick={() => setMobileOpen(false)}>Admin</MobileLink>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </nav>
  );
}

function NavLink({ to, active, children }: { to: string; active: boolean; children: React.ReactNode }) {
  return (
    <Link
      to={to}
      className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
        active
          ? 'bg-lime/30 text-dark'
          : 'text-gray-600 hover:text-dark hover:bg-lime/10'
      }`}
    >
      {children}
    </Link>
  );
}

function MobileLink({ to, onClick, children }: { to: string; onClick: () => void; children: React.ReactNode }) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className="block px-3 py-2 rounded-lg text-base font-medium text-dark hover:bg-lime/20 transition-colors"
    >
      {children}
    </Link>
  );
}
